import { VEHICLE_TIERS } from '@otw/models/inventory'
import {
  conditionOptions,
  getVehicleModelOptions,
  getVehicleSizeOptions,
  getVehicleYearOptions,
  makeOptions,
  vehicleFuelOptions,
  vehicleStatusOptions,
  vehicleTypeOptions,
} from '../../../constants/inventory'
import { verifyProperEmail } from '../../../core/utils/verifyProperEmail'
import states from 'states-us'

export const INVENTORY_V3_NUMBER_FIELDS = [
  'miles',
  'optic_list_price',
  'seller_asking_price',
  'profit',
  'lien_payoff_amount',
  'repair_cost',
  'estimated_repair_cost',
  'shipping_cost',
  'year',
] as const

export const INVENTORY_V3_DATE_FIELDS = ['sold_date', 'date_available'] as const

const INSPECTION_STATUS_OPTIONS = ['N/A', 'Need', 'Pending', 'Complete']
const EQUIPMENT_OPTIONS = ['yes', 'no', 'unknown']
const COLOR_OPTIONS = ['White', 'Blue', 'Yellow', 'Being painted']
const REAR_WHEEL_OPTIONS = ['SRW', 'DRW']
const TITLE_STATE_OPTIONS = states.map(state => state.abbreviation)

const FIELD_LABELS: Record<string, string> = {
  miles: 'Miles',
  optic_list_price: 'List Price',
  seller_asking_price: 'Purchase',
  profit: 'Profit',
  lien_payoff_amount: 'Lien Payoff',
  repair_cost: 'Repair Cost',
  estimated_repair_cost: 'Est. Repair Cost',
  shipping_cost: 'Shipping Cost',
  year: 'Year',
  sold_date: 'Sold Date',
  date_available: 'Date Available',
  email: 'Email',
  phone_number: 'Phone',
  vin: 'VIN',
  type: 'Type',
  make: 'Make',
  model: 'Model',
  size: 'Size',
  fuel: 'Fuel',
  condition: 'Condition',
  tier: 'Tier',
  color: 'Color',
  status: 'Status',
  rear_wheel: 'Rear Wheel',
  mechanical_status: 'Mechanical',
  body_status: 'Body',
  cameras: 'Cameras',
  shelves: 'Shelves',
  gwvr_over_10k: 'GVWR > 10k',
  size_confirmed: 'Size Confirmed',
  title_state: 'Title State',
}

type ParseOk<T> = { ok: true; value: T }
type ParseErr = { ok: false; message: string }
export type ParseResult<T> = ParseOk<T> | ParseErr

const labelFor = (field: string) => FIELD_LABELS[field] ?? field

/** Strip currency / grouping noise for inventory number edit parsing. */
export const normalizeInventoryV3NumberInputString = (value: string): string =>
  value.trim().replace(/[$\s]/g, '').replace(/,/g, '')

/**
 * True while the user is mid-entry (e.g. "12." or ".").
 * DataGrid valueParser must not coerce these to a number — Number("12.") === 12
 * drops the decimal and blocks typing cents.
 */
export const isIncompleteInventoryV3NumberInput = (value: unknown): boolean => {
  if (typeof value !== 'string') return false
  const normalized = normalizeInventoryV3NumberInputString(value)
  if (normalized === '-' || normalized === '.' || normalized === '-.') return true
  return /^-?\d+\.$/.test(normalized)
}

export const parseInventoryV3Number = (value: unknown): ParseResult<number | null> => {
  if (value === null || value === undefined) return { ok: true, value: null }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return { ok: false, message: 'Enter a valid number' }
    return { ok: true, value }
  }
  if (typeof value === 'boolean') return { ok: true, value: Number(value) }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return { ok: true, value: null }
    const normalized = normalizeInventoryV3NumberInputString(trimmed)
    if (normalized === '' || normalized === '-' || normalized === '.' || normalized === '-.' || normalized.toLowerCase() === 'e') {
      return { ok: false, message: 'Enter a valid number' }
    }
    // Trailing "." is incomplete edit state — reject at commit time rather than coerce to int
    if (/^-?\d+\.$/.test(normalized)) {
      return { ok: false, message: 'Enter a valid number' }
    }
    const parsed = Number(normalized)
    if (!Number.isFinite(parsed)) return { ok: false, message: 'Enter a valid number' }
    return { ok: true, value: parsed }
  }
  return { ok: false, message: 'Enter a valid number' }
}

export const parseInventoryV3Date = (value: unknown): ParseResult<string | null> => {
  if (value === null || value === undefined) return { ok: true, value: null }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return { ok: false, message: 'Enter a valid date' }
    return { ok: true, value: value.toISOString() }
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return { ok: true, value: null }
    const date = new Date(trimmed)
    if (Number.isNaN(date.getTime())) return { ok: false, message: 'Enter a valid date' }
    return { ok: true, value: date.toISOString() }
  }
  return { ok: false, message: 'Enter a valid date' }
}

const isBlank = (value: unknown) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '')

const yearOptions = () => new Set(getVehicleYearOptions().map(Number))

const optionsForSelect = (
  field: string,
  context: { make?: string | null; type?: string | null },
): string[] | number[] | null => {
  switch (field) {
    case 'type':
      return vehicleTypeOptions
    case 'make':
      return makeOptions
    case 'model':
      return [...getVehicleModelOptions(context.make ?? ''), 'Unknown']
    case 'size':
      return [...getVehicleSizeOptions(context.type ?? ''), 'Unknown']
    case 'fuel':
      return vehicleFuelOptions
    case 'condition':
      return conditionOptions
    case 'tier':
      return [...VEHICLE_TIERS]
    case 'color':
      return COLOR_OPTIONS
    case 'status':
      return vehicleStatusOptions
    case 'year':
      return [...yearOptions()]
    case 'rear_wheel':
      return REAR_WHEEL_OPTIONS
    case 'mechanical_status':
    case 'body_status':
      return INSPECTION_STATUS_OPTIONS
    case 'cameras':
    case 'shelves':
    case 'gwvr_over_10k':
    case 'size_confirmed':
      return EQUIPMENT_OPTIONS
    case 'title_state':
      return TITLE_STATE_OPTIONS
    default:
      return null
  }
}

const validateSelect = (
  field: string,
  value: unknown,
  context: { make?: string | null; type?: string | null },
): string | null => {
  const options = optionsForSelect(field, context)
  if (!options) return null
  // Historical inventory may lack a title state until the next title-copy upload.
  if (field === 'title_state' && isBlank(value)) return null
  if (field === 'tier' && isBlank(value)) return null
  if (isBlank(value)) return `${labelFor(field)} is required`
  if (field === 'year') {
    const parsed = parseInventoryV3Number(value)
    if (!parsed.ok || parsed.value === null) return `Enter a valid ${labelFor(field).toLowerCase()}`
    if (!yearOptions().has(parsed.value)) {
      return `${labelFor(field)} must be between ${Math.min(...yearOptions())} and ${Math.max(...yearOptions())}`
    }
    return null
  }
  if (!options.map(String).includes(String(value))) {
    return `${labelFor(field)} is not a valid option`
  }
  return null
}

export type InventoryV3PatchContext = {
  make?: string | null
  type?: string | null
}

export type NormalizePatchResult =
  | { ok: true; patch: Record<string, unknown> }
  | { ok: false; message: string }

export const normalizeInventoryV3Patch = (
  patch: Record<string, unknown>,
  context: InventoryV3PatchContext,
): NormalizePatchResult => {
  const next: Record<string, unknown> = { ...patch }
  const make = typeof next.make === 'string' ? next.make : context.make
  const type = typeof next.type === 'string' ? next.type : context.type
  const selectContext = { make, type }

  for (const field of INVENTORY_V3_NUMBER_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(next, field)) continue
    const parsed = parseInventoryV3Number(next[field])
    if (!parsed.ok) return { ok: false, message: `${labelFor(field)}: ${parsed.message}` }
    if (parsed.value !== null && parsed.value < 0 && field !== 'profit') {
      return { ok: false, message: `${labelFor(field)} cannot be negative` }
    }
    if (field === 'year' && parsed.value !== null) {
      const years = yearOptions()
      if (!years.has(parsed.value)) {
        return {
          ok: false,
          message: `Year must be between ${Math.min(...years)} and ${Math.max(...years)}`,
        }
      }
    }
    next[field] = parsed.value
  }

  for (const field of INVENTORY_V3_DATE_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(next, field)) continue
    const parsed = parseInventoryV3Date(next[field])
    if (!parsed.ok) return { ok: false, message: `${labelFor(field)}: ${parsed.message}` }
    next[field] = parsed.value
  }

  if (Object.prototype.hasOwnProperty.call(next, 'email')) {
    const email = next.email
    if (!isBlank(email)) {
      if (typeof email !== 'string' || !verifyProperEmail(email.trim())) {
        return { ok: false, message: 'Enter a valid email address' }
      }
      next.email = email.trim()
    } else {
      next.email = ''
    }
  }

  if (Object.prototype.hasOwnProperty.call(next, 'phone_number')) {
    const phone = next.phone_number
    if (!isBlank(phone)) {
      const digits = String(phone).replace(/\D/g, '')
      if (digits.length !== 10) {
        return { ok: false, message: 'Phone number must be exactly 10 digits' }
      }
      next.phone_number = digits
    } else {
      next.phone_number = ''
    }
  }

  if (Object.prototype.hasOwnProperty.call(next, 'vin')) {
    const vin = next.vin
    if (!isBlank(vin)) {
      const normalized = String(vin).trim().toUpperCase()
      if (normalized.length !== 17) {
        return { ok: false, message: 'VIN must be exactly 17 characters' }
      }
      next.vin = normalized
    } else {
      next.vin = ''
    }
  }

  if (Object.prototype.hasOwnProperty.call(next, 'title_state')) {
    next.title_state = isBlank(next.title_state) ? null : String(next.title_state).trim()
  }

  if (Object.prototype.hasOwnProperty.call(next, 'tier')) {
    next.tier = isBlank(next.tier) ? null : String(next.tier)
  }

  for (const field of Object.keys(next)) {
    if (INVENTORY_V3_NUMBER_FIELDS.includes(field as (typeof INVENTORY_V3_NUMBER_FIELDS)[number])) continue
    const selectError = validateSelect(field, next[field], selectContext)
    if (selectError) return { ok: false, message: selectError }
  }

  return { ok: true, patch: next }
}
